import { CastReceiverContext } from 'chromecast-caf-receiver/cast.framework';
import { Event as SystemEvent } from 'chromecast-caf-receiver/cast.framework.system';
import type { CastSession } from '../types/session';
import { Hermes } from '../network';
import { Analytics } from '../analytics';
import { Logger } from '../logger';
import { setDatadogUser } from '../monitoring/datadog';

/**
 * User channel — receives the user token from the sender app via a custom
 * Cast message channel, then fetches user profile and language preferences.
 *
 * Catalog highlights are fetched separately by the standby screen module
 * (no auth needed), so the standby UI can show content before the sender
 * sends a token.
 *
 * **Protocol (sender side):**
 * 1. After `SESSION_STARTED`, sender sends
 *    `{ userToken: "<jwt>", refreshToken?: "<token>" }` on namespace
 *    `urn:x-cast:br.com.brasilparalelo.user`. `refreshToken` is optional —
 *    a portion of the app's logged-in base has stored sessions without a
 *    refresh token, and we do not want to force them to re-authenticate.
 * 2. Receiver authenticates with Hermes (refreshing the pair when possible,
 *    using the access token directly otherwise) and populates session state.
 * 3. Callback fires so `app.ts` can update the standby screen with user info.
 */

const LOG_TAG = 'USER_CHANNEL';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Cast custom message namespace for receiving the user token. */
export const USER_CHANNEL_NAMESPACE =
  'urn:x-cast:br.com.brasilparalelo.user';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Payload sent by the sender on the user channel.
 *
 * `refreshToken` is optional: there is a logged-in base in the app whose
 * stored sessions don't carry a refresh token, and we don't want to force
 * those users to re-authenticate. When absent, the receiver skips the
 * RefreshToken mutation and uses `userToken` directly as a Bearer token.
 */
interface UserTokenMessage {
  userToken: string;
  refreshToken?: string;
}

// ---------------------------------------------------------------------------
// Core: handle incoming user token
// ---------------------------------------------------------------------------

/**
 * Processes a user-token message from the sender.
 *
 * Flow:
 * 1. If `refreshToken` is present, call RefreshToken mutation to exchange
 *    sender tokens for a fresh pair (throws on failure) and store the new
 *    pair. Otherwise, use the sender's `userToken` directly — this path
 *    exists to support legacy logged-in sessions in the app that don't
 *    carry a refresh token.
 * 2. Set auth token on Hermes
 * 3. Fetch user profile → populate session (try/catch for graceful degradation)
 * 4. Set Analytics userId (graceful degradation)
 * 5. Fetch language preferences → update session defaults (try/catch for graceful degradation)
 */
async function handleUserToken(
  session: CastSession,
  message: UserTokenMessage,
): Promise<void> {
  let accessToken: string;

  if (message.refreshToken) {
    // -- 1a. Refresh path: exchange sender tokens for a fresh pair --
    const refreshResponse = await Hermes.client.RefreshToken({
      input: { refresh_token: message.refreshToken },
    });

    if (refreshResponse.refresh_token.__typename === 'error') {
      throw new Error(`Token refresh failed: ${refreshResponse.refresh_token.message}`);
    }

    accessToken = refreshResponse.refresh_token.token;
    session.userToken = accessToken;
    session.refreshToken = refreshResponse.refresh_token.refresh;
    Logger.info(LOG_TAG, 'New token pair stored from RefreshToken mutation');
  } else {
    // -- 1b. Legacy path: sender has no refresh token (older logged-in sessions
    // in the app that we don't want to force-reauth). Use the access token
    // directly — it will work until it expires, with no in-session renewal. --
    accessToken = message.userToken;
    session.userToken = accessToken;
    session.refreshToken = '';
    Logger.info(LOG_TAG, 'No refresh token from sender; using access token directly (legacy session)');
  }

  // -- 2. Set auth token on Hermes --
  Hermes.setAuthToken(accessToken);
  Logger.info(LOG_TAG, 'Auth token set');

  // -- 3. User profile --
  try {
    const { user } = await Hermes.client.GetUser();
    session.userId = user.id;
    session.userName = user.name;
    session.userAvatar = user.avatar.image;

    Logger.info(LOG_TAG, 'User profile loaded', {
      userId: user.id,
      userName: user.name,
    });
  } catch (err: unknown) {
    Logger.error(LOG_TAG, 'Failed to fetch user profile', err);
  }

  // -- 4. Analytics + Datadog user context --
  if (session.userId) {
    Analytics.setUserId(session.userId);
    Analytics.logEvent(Analytics.eventNames.CAST_USER_CONNECTED, {
      user_name: session.userName,
    });
    setDatadogUser(session.userId, session.userName);
    Logger.debug(LOG_TAG, 'Analytics & Datadog userId set', { userId: session.userId });
  }

  // -- 5. Language preferences --
  try {
    const { user } = await Hermes.client.GetUserLanguageSettings();
    const { audio, subtitles } = user.settings.player.language;
    session.userAudio = audio;
    session.userSub = subtitles;

    Logger.debug(LOG_TAG, 'Language preferences loaded', {
      audio,
      subtitles,
    });
  } catch (err: unknown) {
    Logger.error(LOG_TAG, 'Failed to fetch language preferences', err);
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Registers the user-channel custom message listener on the Cast context.
 *
 * @param context - Cast receiver context instance
 * @param session - Mutable session state (populated with user data on message)
 * @param onUserReady - Callback fired after user profile is loaded. Used by
 *   `app.ts` to push the user profile to the standby screen.
 */
export function registerUserChannel(
  context: CastReceiverContext,
  session: CastSession,
  onUserReady?: () => void,
): void {
  context.addCustomMessageListener(
    USER_CHANNEL_NAMESPACE,
    (event: SystemEvent) => {
      const data = event.data as UserTokenMessage | undefined;
      const senderId = (event as unknown as { senderId: string }).senderId;

      if (!data?.userToken) {
        Logger.warn(LOG_TAG, 'Received message without userToken', { data });
        return;
      }

      Logger.info(LOG_TAG, 'User token received from sender', {
        hasRefreshToken: Boolean(data.refreshToken),
      });

      handleUserToken(session, data)
        .then(() => {
          context.sendCustomMessage(USER_CHANNEL_NAMESPACE, senderId, {
            type: 'AUTH_RESULT',
            status: 'ready',
            userId: session.userId,
            userName: session.userName,
          });
          if (onUserReady) {
            onUserReady();
          }
        })
        .catch((err: unknown) => {
          Logger.error(LOG_TAG, 'Token refresh failed', err);
          context.sendCustomMessage(USER_CHANNEL_NAMESPACE, senderId, {
            type: 'AUTH_RESULT',
            status: 'error',
            error: String(err),
          });
        });
    },
  );

  Logger.info(LOG_TAG, 'User channel registered');
}
